import React, { useEffect, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import SEO from './SEO';
import './PolicyModal.css';

const TYPES = [
  { key: 'privacy', label: 'Privacy Policy' },
  { key: 'terms', label: 'Terms & Conditions' },
  { key: 'acceptance', label: 'User Acceptance Policy' },
  { key: 'deletion', label: 'Account Deletion Terms' },
];

const APP_LABELS = {
  user: 'Customer Policy',
  rider: 'Rider & Driver Policy',
  merchant: 'Merchant Policy',
  vendor: 'Vendor Policy',
};

// Stable, generic-but-accurate meta descriptions. Deliberately independent
// of the actual policy text (which is fetched live from Firestore and can
// change at any time), so search/social previews never go stale between
// prerenders just because an admin updated a policy document.
const APP_SEO = {
  user: {
    title: 'Customer Policy | BikesDrop Privacy, Terms & Account Policies',
    description: 'How BikesDrop handles your orders, payments, data and support as a customer, plus our Terms & Conditions, Privacy Policy, and account deletion terms.',
  },
  rider: {
    title: 'Rider & Driver Policy | BikesDrop Terms & Conduct',
    description: 'Requirements, conduct, earnings and support terms for everyone riding for BikesDrop, plus our Terms & Conditions, Privacy Policy, and account deletion terms.',
  },
  merchant: {
    title: 'Merchant Policy | BikesDrop Terms & Payout Policies',
    description: 'Terms for listing your business, receiving orders and getting paid through BikesDrop, plus our Terms & Conditions, Privacy Policy, and account deletion terms.',
  },
  vendor: {
    title: 'Vendor Policy | BikesDrop Delivery Partnership Policies',
    description: 'Terms for BikesDrop delivery partner vendors, covering shop applications, deliveries and point of sale, plus our Terms & Conditions, Privacy Policy, and account deletion terms.',
  },
};

function formatDate(timestamp) {
  if (!timestamp?.toDate) return null;
  return timestamp.toDate().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

function isPdf(policy) {
  const nameOrUrl = `${policy?.documentName || ''} ${policy?.documentUrl || ''}`.toLowerCase();
  return nameOrUrl.includes('.pdf');
}

// Extracts readable text from a PDF entirely in the browser, so an admin who
// uploaded a policy as a PDF still gets a formatted reading page instead of
// visitors being dropped into a raw file viewer. Falls back silently if the
// file can't be parsed (e.g. a scanned/image-only PDF).
async function extractPdfText(url) {
  const pdfjsLib = await import('pdfjs-dist');
  pdfjsLib.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

  const res = await fetch(url);
  const buffer = await res.arrayBuffer();
  const pdf = await pdfjsLib.getDocument({ data: buffer }).promise;

  const pages = [];
  for (let i = 1; i <= pdf.numPages; i += 1) {
    const page = await pdf.getPage(i);
    const textContent = await page.getTextContent();
    pages.push(textContent.items.map((item) => item.str).join(' '));
  }
  return pages.join('\n\n').trim();
}

export default function PolicyModal() {
  const { appKey: rawAppKey } = useParams();
  const navigate = useNavigate();
  const appKey = APP_LABELS[rawAppKey] ? rawAppKey : null;
  const [docs, setDocs] = useState(null);
  const [activeType, setActiveType] = useState('privacy');
  const [error, setError] = useState(false);
  const [extracted, setExtracted] = useState({});
  const [extracting, setExtracting] = useState(false);
  const [extractFailed, setExtractFailed] = useState(false);

  useEffect(() => {
    if (!appKey) return;
    let cancelled = false;
    setDocs(null);
    setError(false);
    setActiveType('privacy');
    setExtracted({});
    setExtractFailed(false);

    Promise.all(
      TYPES.map(async (t) => {
        const snap = await getDoc(doc(db, 'bikesdrop_policies', `${appKey}_${t.key}`));
        return [t.key, snap.exists() ? snap.data() : null];
      })
    )
      .then((entries) => {
        if (cancelled) return;
        setDocs(Object.fromEntries(entries));
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });

    return () => {
      cancelled = true;
    };
  }, [appKey]);

  const active = docs?.[activeType];
  const needsExtraction =
    active && active.mode === 'document' && !active.content && active.documentUrl && isPdf(active);

  useEffect(() => {
    if (!needsExtraction || extracted[activeType]) return undefined;
    let cancelled = false;
    setExtracting(true);
    setExtractFailed(false);

    extractPdfText(active.documentUrl)
      .then((text) => {
        if (cancelled) return;
        if (text) {
          setExtracted((prev) => ({ ...prev, [activeType]: text }));
        } else {
          setExtractFailed(true);
        }
      })
      .catch(() => {
        if (!cancelled) setExtractFailed(true);
      })
      .finally(() => {
        if (!cancelled) setExtracting(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsExtraction, activeType, active?.documentUrl]);

  useEffect(() => {
    const onKey = (e) => e.key === 'Escape' && navigate('/');
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [navigate]);

  // An unrecognized app key (typo, or an old bookmarked link) goes home
  // rather than rendering an empty policy shell with nothing in it.
  if (!appKey) return <Navigate to="/" replace />;

  const readableText = active?.content || extracted[activeType];
  const seo = APP_SEO[appKey];

  return (
    <div className="policy-page">
      <SEO title={seo.title} description={seo.description} path={`/policies/${appKey}/`} />
      <div className="policy-page__header">
        <div className="container policy-page__header-inner">
          <Link className="policy-page__back" to="/">
            <span className="policy-page__back-arrow">&larr;</span>
            Back to site
          </Link>
          <span className="policy-page__eyebrow">{APP_LABELS[appKey]}</span>
        </div>
      </div>

      <div className="policy-page__tabbar">
        <div className="container policy-page__tabbar-inner">
          {TYPES.map((t) => (
            <button
              key={t.key}
              className={`policy-page__tab ${activeType === t.key ? 'policy-page__tab--active' : ''}`}
              onClick={() => setActiveType(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div className="policy-page__body">
        <div className="policy-page__article">
          <h1>{TYPES.find((t) => t.key === activeType)?.label}</h1>

          {error && (
            <p className="policy-page__empty">This policy could not be loaded right now. Please try again shortly.</p>
          )}

          {!error && docs === null && <p className="policy-page__empty">Loading policy content...</p>}

          {!error && docs !== null && !active && (
            <p className="policy-page__empty">This policy has not been published yet. Please check back soon.</p>
          )}

          {!error && active && (
            <>
              {active.updatedAt && (
                <p className="policy-page__updated">Last updated {formatDate(active.updatedAt)}</p>
              )}

              {readableText && <div className="policy-page__text">{readableText}</div>}

              {!readableText && extracting && (
                <p className="policy-page__empty">Preparing this policy for reading...</p>
              )}

              {!readableText && !extracting && (extractFailed || !isPdf(active)) && active.documentUrl && (
                <div className="policy-page__fallback">
                  <p>
                    A formatted preview isn&rsquo;t available for this document yet. You can still open
                    the original file below.
                  </p>
                  <a
                    className="policy-page__fallback-link"
                    href={active.documentUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Open {active.documentName || 'document'} &rarr;
                  </a>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
